// 自重トレ大全: 「あなたの自重メニューを作る」
// レベル・目的・週の回数・時間・道具から、1週間のメニューを組む（計算はすべてこの端末の中）。
// 組み方の考え方（記事 /blogs/jiju/bodyweight-reps-sets-frequency と同じ）:
//  - 週2〜3回は毎回全身、週4回は上半身の日と下半身の日に分ける（同じ部位は1日以上あける）
//  - 1種目 2〜3 セット。引き締めが目的の日は最後に心拍が上がる種目を足す
//  - レベルに合う種目だけを選び、鉄棒・椅子がないときは使わない種目にする
import MOVES from './jiju-moves.js';

const POOL = {
  push: [['incline-push-up', 1, 'chair'], ['knee-push-up', 1], ['push-up', 2], ['wide-push-up', 2], ['pike-push-up', 2], ['diamond-push-up', 3], ['decline-push-up', 3, 'chair'], ['bench-dips', 2, 'chair']],
  pull: [['superman', 1], ['bird-dog', 1], ['inverted-row', 2, 'bar'], ['chin-up', 3, 'bar'], ['pull-up', 3, 'bar']],
  legs: [['squat', 1], ['sumo-squat', 1], ['reverse-lunge', 1], ['wall-sit', 1], ['lunge', 2], ['jump-squat', 2], ['bulgarian-split-squat', 3, 'chair'], ['pistol-squat', 3]],
  hinge: [['glute-bridge', 1], ['single-leg-glute-bridge', 2], ['calf-raise', 1]],
  core: [['plank', 1], ['dead-bug', 1], ['crunch', 1], ['side-plank', 2], ['leg-raise', 2], ['bicycle-crunch', 2]],
  cardio: [['jumping-jack', 1], ['mountain-climber', 2], ['burpee', 2]],
};
const HOLD = new Set(['plank', 'side-plank', 'wall-sit', 'jumping-jack', 'mountain-climber']);
const PER_SIDE = new Set(['lunge', 'reverse-lunge', 'bulgarian-split-squat', 'pistol-squat', 'single-leg-glute-bridge', 'side-plank', 'dead-bug', 'bird-dog', 'bicycle-crunch']);
const DAY = ['月', '火', '水', '木', '金', '土', '日'];
const SCHEDULE = { 2: [0, 3], 3: [0, 2, 4], 4: [0, 1, 3, 4] };

// レベルに合う種目を、上限レベルに近いものから選ぶ（同じ週で偏らないよう、日ごとにずらす）
function pick(cat, o, n, shift) {
  const ok = POOL[cat].filter(([id, lv, gear]) => lv <= o.lv && (!gear || o[gear]));
  const sorted = ok.slice().sort((a, b) => b[1] - a[1] || 0);
  const top = sorted.filter(([, lv]) => lv >= Math.max(1, o.lv - 1));
  const list = top.length >= n ? top : sorted;
  const out = [];
  for (let i = 0; i < n && list.length; i++) out.push(list[(i + shift) % list.length][0]);
  return [...new Set(out)];
}

function dose(id, o) {
  const sets = o.min === 10 ? 2 : o.min === 20 ? (o.lv === 1 ? 2 : 3) : 3;
  if (HOLD.has(id)) {
    const sec = { 1: 20, 2: 30, 3: 45 }[o.lv] + (o.goal === 'burn' && (id === 'jumping-jack' || id === 'mountain-climber') ? 10 : 0);
    return `${sec}秒×${sets}`;
  }
  let reps = { 1: 10, 2: 12, 3: 10 }[o.lv];
  if (o.goal === 'burn') reps += 3;
  if (o.goal === 'health') reps = Math.min(reps, 10);
  if (/pull-up|chin-up|pistol/.test(id)) reps = { 1: 3, 2: 5, 3: 6 }[o.lv];
  return `${reps}回${PER_SIDE.has(id) ? '（左右）' : ''}×${sets}`;
}

export function plan(o) {
  const days = SCHEDULE[o.days];
  const count = o.min === 10 ? 3 : o.min === 20 ? 4 : 6;
  return DAY.map((d, i) => {
    const k = days.indexOf(i);
    if (k < 0) return { day: d, rest: true };
    let items = [];
    let title = '全身';
    if (o.days === 4) {
      const upper = k % 2 === 0;
      title = upper ? '上半身＋体幹' : '下半身＋体幹';
      if (upper) items = [...pick('push', o, 2, k), ...pick('pull', o, 1, k), ...pick('core', o, count >= 6 ? 2 : 1, k)];
      else items = [...pick('legs', o, 2, k), ...pick('hinge', o, 1, k), ...pick('core', o, count >= 6 ? 2 : 1, k + 1)];
    } else {
      items = [...pick('legs', o, 1, k), ...pick('push', o, 1, k), ...pick('hinge', o, 1, k), ...pick('core', o, 1, k)];
      if (count >= 6) items.push(...pick('pull', o, 1, k), ...pick('legs', o, 1, k + 1));
    }
    if (o.goal === 'burn') items.push(...pick('cardio', o, 1, k));
    items = [...new Set(items)].slice(0, count + (o.goal === 'burn' ? 1 : 0));
    return { day: d, title, items: items.map((id) => ({ id, name: MOVES[id].name, dose: dose(id, o) })) };
  });
}

function read(form) {
  const f = new FormData(form);
  return { lv: +f.get('lv'), goal: f.get('goal'), days: +f.get('days'), min: +f.get('min'), bar: !!f.get('bar'), chair: !!f.get('chair') };
}

function render(box, o) {
  const p = plan(o);
  const rest = o.min === 10 ? '休憩 30秒' : '休憩 60〜90秒';
  box.innerHTML = p.map((d) => d.rest
    ? `<div class="jj-day jj-day--rest"><h3>${d.day}<small>休み</small></h3><p style="margin:0;font-size:13px">歩く・ストレッチなど軽く体を動かす日</p></div>`
    : `<div class="jj-day"><h3>${d.day}　${d.title}<small>${rest}</small></h3><ol>${d.items.map((x) => `<li><a href="/pages/jiju-zukan#${x.id}">${x.name}</a><span>${x.dose}</span></li>`).join('')}</ol></div>`).join('');
}

if (typeof document !== 'undefined') {
  for (const form of document.querySelectorAll('[data-jj-menu]')) {
    const box = form.parentElement.querySelector('[data-jj-plan]');
    const go = () => render(box, read(form));
    form.addEventListener('change', go);
    go();
  }
}
